/**
 * CtfBanner — "Collect The Flags is now live, go try it!" announcement.
 *
 * Shown on the v1 Home + Dashboard. Auto-appears only once an admin has opened
 * the CTF mode (page gate on either platform, or the viewer is staff), so it
 * never advertises a locked mode. Dismissible (remembered in localStorage).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Flag, X, ChevronRight, Sparkles } from "lucide-react";
import useWcModeVisibility from "../hooks/useWcModeVisibility";

const DISMISS_KEY = "ctf_live_banner_dismissed_v1";

export default function CtfBanner({ to = "/wc2026/ctf" }) {
  const m = useWcModeVisibility("ctf", "mobile");
  const c = useWcModeVisibility("ctf", "console");
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch (_) { return false; }
  });

  if (m.loading || c.loading) return null;
  const available = m.isStaff || m.effective || c.effective;
  if (!available || dismissed) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
    setDismissed(true);
  };

  return (
    <Link
      to={to}
      data-testid="ctf-live-banner"
      className="block relative overflow-hidden rounded-2xl border border-[#60A5FA]/50 bg-gradient-to-r from-[#1E3A8A] via-[#0F172A] to-[#020617] px-4 py-3.5 group"
      style={{ boxShadow: "0 12px 30px -16px rgba(96,165,250,0.6)" }}
    >
      <div aria-hidden className="absolute -top-10 -right-6 w-40 h-40 rounded-full bg-[#60A5FA]/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-center gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-[#60A5FA]/20 border border-[#60A5FA]/50">
          <Flag className="w-5 h-5 text-[#60A5FA]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.3em] text-[#60A5FA]">
            <Sparkles className="w-3 h-3" /> New mode · Live now
          </div>
          <div className="font-heading text-base sm:text-lg uppercase tracking-wider text-white leading-tight">
            Collect The Flags is here
          </div>
          <div className="text-[11.5px] text-white/65 leading-tight mt-0.5">
            Pure national squads · trade flags with other players · collect all 48. Go try it!
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 shrink-0 px-3 py-2 rounded-full bg-[#60A5FA] text-[#020617] text-[10px] font-mono uppercase tracking-widest font-bold group-hover:gap-2 transition-all">
          Play <ChevronRight className="w-3.5 h-3.5" />
        </span>
        <button
          type="button"
          onClick={dismiss}
          data-testid="ctf-live-banner-dismiss"
          aria-label="Dismiss"
          className="shrink-0 ml-1 text-white/40 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </Link>
  );
}
